import CustomField from '../../../../../../../../elements/form/CustomField'
import Button from '../../../../../../../../elements/Button'
import { Formik, Form } from 'formik'
import { useState, useEffect } from 'react'
import { useFormikContext } from 'formik'
import { useModalContext } from '../../../../../../../../../providers/modalProvider'
import axios from '/components/configs/axios'
import ThankYou from './ThankYou'
import StripeComponent from '/components/pages/single-property/StripeComponent'
import styles from './ThankYou/styles.module.scss'
import Column from '../../../../../../../../layout/Column'
import Columns from '../../../../../../../../layout/Columns'
import Details from './Details'
import IconContact from '/public/icons/icon-step-contact.svg'
import IconPayment from '/public/icons/icon-step-payment.svg'
import { useResponsive } from '../../../../../../../../../providers/ResponsiveContext'
import * as Yup from 'yup'
import { useRouter } from 'next/router'
import { useSearchContext } from '../../../../../../../../../providers/SearchContext'
import {
  getUserContact,
  saveUserContact
} from '/components/helpers/userContactStorage'

// Helper component to sync updatedPrice with Formik's total field
// This is extracted to a separate component to properly use hooks
// (hooks cannot be called inside render props)
const FormTotalSync = ({ updatedPrice }) => {
  const { setFieldValue } = useFormikContext()

  useEffect(() => {
    if (updatedPrice?.finalCost !== undefined) {
      setFieldValue('total', updatedPrice.finalCost)
    }
  }, [updatedPrice, setFieldValue])

  return null
}

const ModalContent = ({
  propertyId,
  guests,
  total,
  bedrooms,
  property,
  taxesAndFees,
  totalCostPerNights,
  totalWithoutStripe,
  bonuses,
  numberOfFreeNights,
  freeNightsCost,
  freeNightsOldPrice,
  translations
}) => {
  const router = useRouter()
  const { search } = useSearchContext()
  const startDate = search.startDate
  const endDate = search.endDate
  const ReservationSchema = Yup.object().shape({
    firstName: Yup.string()
      .max(25)
      .matches(
        /^'?\p{L}+(?:[' ]\p{L}+)*'?$/u,
        translations.form.error.validName[router.locale]
      )
      .required(translations.form.error.requiredFirstName[router.locale]),
    lastName: Yup.string()
      .max(25)
      .matches(
        /^'?\p{L}+(?:[' ]\p{L}+)*'?$/u,
        translations.form.error.validName[router.locale]
      )
      .required(translations.form.error.requiredLastName[router.locale]),
    email: Yup.string()
      .matches(
        /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
        translations.form.error.validEmail[router.locale]
      )
      .max(254)
      .email(translations.form.error.validEmail[router.locale])
      .required(translations.form.error.requiredEmail[router.locale])
  })

  const { handleCloseModal } = useModalContext()
  const { isMobile } = useResponsive()

  const [plans, setPlans] = useState('details')
  const [skip, setSkip] = useState(false)
  const [coupon, setCoupon] = useState(false)
  const [updatedPrice, setUpdatedPrice] = useState(false)
  const [reservationId, setReservationId] = useState('')
  const [reservationData, setReservationData] = useState(null)
  const [paymentError, setPaymentError] = useState(null)
  const [savedContact, setSavedContact] = useState({})
  const isThankYou = plans === 'thankYou'

  // Load saved contact info on mount
  useEffect(() => {
    const contact = getUserContact()
    setSavedContact(contact)
  }, [])

  const detailsProps = {
    guests,
    total,
    startDate,
    endDate,
    bedrooms,
    property,
    updatedPrice,
    setUpdatedPrice,
    setCoupon,
    taxesAndFees,
    totalCostPerNights,
    totalWithoutStripe,
    bonuses,
    numberOfFreeNights,
    freeNightsCost,
    freeNightsOldPrice,
    translations
  }

  const formInitialValues = {
    id: propertyId,
    startDate: startDate,
    endDate: endDate,
    bedrooms: bedrooms,
    total: Number(total),
    guests: guests,
    bonuses: bonuses,
    propertyName: property?.propertyTitle,
    firstName: savedContact.firstName || '',
    lastName: savedContact.lastName || '',
    email: savedContact.email || '',
    phone: savedContact.phone || null,
    car: null,
    transport: null,
    assistance: null,
    food: null,
    reservation: null
  }

  const [submitted, setSubmitted] = useState(formInitialValues)

  // Handler to save contact info on field blur
  const handleFieldBlur = (fieldName, value) => {
    if (value) {
      saveUserContact({ [fieldName]: value })
    }
  }

  const handleSubmit = (values, { setSubmitting }) => {
    setSubmitting(true)
    if (plans === 'details') {
      setPaymentError(null)
      // Save contact info when moving to next step
      saveUserContact({
        firstName: values.firstName,
        lastName: values.lastName,
        email: values.email
      })
      axios
        .post('reservation/payment-intent', {
          params: values
        })
        .then(() => {
          //stripe verification if previous is valid
          //if everything checks, set Plans (thank you form)
          setPlans('payment')
          setSubmitted({
            ...submitted,
            email: values.email,
            firstName: values.firstName,
            lastName: values.lastName,
            bedrooms: values.bedrooms,
            guests: values.guests,
            startDate: values.startDate,
            endDate: values.endDate,
            bonuses: values.bonuses,
            propertyName: values.propertyName
          })
        })
        .catch((err) => {
          console.error('Payment intent creation failed:', err)
          setPaymentError(
            err.response?.data?.message ||
              translations.form.error.paymentIntentFailed?.[router.locale] ||
              'Unable to initialize payment. Please try again.'
          )
        })
        .finally(() => {
          setSubmitting(false)
        })
      return
    }

    if (plans === 'thankYou') {
      axios
        .post('reservation/questions', {
          ...values,
          coupon,
          bonuses,
          total,
          freeNights: numberOfFreeNights,
          freeNightlyRate: freeNightsCost,
          initialPrice: freeNightsOldPrice,
          reservationId: reservationId
        })
        .then(() => {
          handleCloseModal()
        })
        .catch((err) => console.error(err))
        .finally(() => {
          setSubmitting(false)
        })
      return
    }

    setSubmitting(false)
  }

  if (plans !== 'finish') {
    return (
      <Columns variant={[isThankYou && 'no-gutter']}>
        <Column
          variant={[isThankYou && 'no-padding']}
          spans={isThankYou || isMobile ? 12 : 7}
        >
          <Formik
            onSubmit={handleSubmit}
            enableReinitialize
            validationSchema={ReservationSchema}
            initialValues={formInitialValues}
          >
            {({ values, setFieldValue, errors, touched, isSubmitting }) => {
              // Note: FormTotalSync handles syncing updatedPrice with form state
              // It's rendered as a component to properly use hooks (see component above)

              if (plans === 'details') {
                return (
                  <Form className={styles.form}>
                    <FormTotalSync updatedPrice={updatedPrice} />
                    <IconContact />

                    <h2 className={styles.form__title}>
                      {translations.contactDetails[router.locale]}
                    </h2>

                    <div>
                      <CustomField
                        variant={['hidden']}
                        readOnly
                        name="id"
                        value={propertyId}
                      />

                      <CustomField
                        variant={['hidden']}
                        readOnly
                        name="startDate"
                        value={startDate}
                      />

                      <CustomField
                        variant={['hidden']}
                        readOnly
                        name="endDate"
                        value={endDate}
                      />

                      <CustomField
                        variant={['hidden']}
                        readOnly
                        name="bedrooms"
                        value={bedrooms}
                      />

                      <CustomField
                        variant={['hidden']}
                        readOnly
                        name="total"
                        value={Number(total)}
                      />

                      <CustomField
                        variant={['full-width']}
                        required
                        maxLength={20}
                        name="firstName"
                        placeholder={
                          translations.form.fields.firstName[router.locale]
                        }
                        className={styles.form__inputs}
                        onChange={(e) => {
                          setFieldValue(
                            'firstName',
                            e.target.value.replace(/[0-9]/g, '')
                          )
                        }}
                        onBlur={(e) =>
                          handleFieldBlur('firstName', e.target.value)
                        }
                        error={errors.firstName && touched?.firstName}
                        withError
                      />

                      <CustomField
                        variant={['full-width']}
                        maxLength={20}
                        required
                        name="lastName"
                        placeholder={
                          translations.form.fields.lastName[router.locale]
                        }
                        className={styles.form__inputs}
                        onChange={(e) => {
                          setFieldValue(
                            'lastName',
                            e.target.value.replace(/[0-9]/g, '')
                          )
                        }}
                        onBlur={(e) =>
                          handleFieldBlur('lastName', e.target.value)
                        }
                        error={errors.lastName && touched?.lastName}
                        withError
                      />

                      <CustomField
                        variant={['full-width']}
                        maxLength={254}
                        name="email"
                        required
                        placeholder={
                          translations.form.fields.email[router.locale]
                        }
                        type="email"
                        className={styles.form__inputs}
                        onBlur={(e) => handleFieldBlur('email', e.target.value)}
                        error={errors.email && touched.email}
                        valid={values.email && !errors.email && touched.email}
                        withError
                      />
                    </div>

                    {paymentError && (
                      <div className={styles.form__error} role="alert">
                        {paymentError}
                      </div>
                    )}

                    <Button
                      disabled={
                        values.email === null ||
                        values.firstName === null ||
                        values.lastName === null ||
                        isSubmitting
                      }
                    >
                      {isSubmitting
                        ? translations.form.fields.processing?.[
                            router.locale
                          ] || 'Processing...'
                        : translations.form.fields.continue[router.locale]}
                    </Button>
                  </Form>
                )
              }
              if (plans === 'payment') {
                return (
                  <div className={styles.form}>
                    <IconPayment />

                    <h2 className={styles.form__title}>
                      {translations.stripe.paymentDetails[router.locale]}
                    </h2>

                    <StripeComponent
                      translations={translations.stripe}
                      setReservationId={setReservationId}
                      setPlans={setPlans}
                      setSkip={setSkip}
                      setReservationData={setReservationData}
                      updatedPrice={updatedPrice}
                      coupon={coupon}
                      freeNights={numberOfFreeNights}
                      freeNightlyRate={freeNightsCost}
                      initialPrice={freeNightsOldPrice}
                      {...submitted}
                      propertyId={detailsProps.property?.resource_id}
                      listingId={detailsProps.property?.id}
                      total={
                        updatedPrice
                          ? Number(updatedPrice.finalCost)
                          : Number(total)
                      }
                    />
                  </div>
                )
              }

              if (isThankYou) {
                return (
                  <ThankYou
                    translations={translations.thankYou}
                    setFieldValue={setFieldValue}
                    reservationData={reservationData}
                    isSubmitting={isSubmitting}
                  />
                )
              }
            }}
          </Formik>
        </Column>

        {!isThankYou && (
          <Column spans={isMobile ? 12 : 5}>
            <Details {...detailsProps} />
          </Column>
        )}
      </Columns>
    )
  }

  return (
    <div className={styles.finish}>
      <h2 className={styles.finish__title}>
        {skip
          ? translations.skip.title1[router.locale]
          : translations.skip.title2[router.locale]}
      </h2>
      <span className={styles.finish__subtitle}>
        {skip
          ? translations.skip.subtitle1[router.locale]
          : translations.skip.subtitle2[router.locale]}
      </span>
      <div className={styles.finish__separator}></div>
      <p className={styles.finish__description}>
        {translations?.skip.questions[router.locale]}
        <a rel="nofollow" href="mailto:reservations@sxmsir.com">
          reservations@sxmsir.com
        </a>
      </p>

      <Button
        href="/guest-experience"
        onClick={() => {
          handleCloseModal()
        }}
        arrow={false}
        variant={['color-accent']}
      >
        {translations.skip.button[router.locale]}
      </Button>
    </div>
  )
}

export default ModalContent
